/**
 * The report's clinical typography and furniture.
 *
 * This is the design of the printed specimen: a letterhead, ruled data tables
 * with a reference range beside every reading, numbered findings sections, and
 * an impression that leads to recommendations. It replaces a card-and-chip
 * dashboard layout, which read as a product screen rather than as a clinical
 * document — and a patient handing this to a physiotherapist is handing over a
 * document, not a screenshot.
 *
 * One stylesheet, scoped to `.clinical`, used by both the on-screen report and
 * its printed form. The screen IS the print layout here; "Print / Save PDF" is
 * the browser's own dialog, so anything that only looked right on screen would
 * be wrong on paper half the time.
 */

import React from 'react';
import {
  bandScaleFor, bilateralPosition, bilateralScaleFor, positionOn, rangeForAngle,
  severityColor, severityCutsFor, severityForAngle,
} from '../../lib/metricBands';
import { CLINICAL_CSS } from './clinicalCss';
import { referenceText } from '../../lib/report/reference';

export { CLINICAL_CSS };


/**
 * Clinic letterhead.
 *
 * The KinetiQ mark identifies the system that produced the measurements; the
 * clinic's own name is what the document is issued under, and it is set
 * beneath the mark rather than replaced by it. A clinic with its own artwork
 * would go here too, but no vendor logo is stored against a vendor record yet,
 * so the name is what there is to print.
 */
export function Letterhead({ clinic, unit = 'Biomechanical Assessment Unit', address, logo, action = null }) {
  return (
    <div className="letterhead">
      {logo
        ? <img className="brand" src={logo} alt="KinetiQ Footcare" />
        : <div className="mark">K</div>}
      <div className="who">
        {logo
          ? <p className="sub">{clinic || 'KinetiQ Footcare'}</p>
          : <h1>{clinic || 'KinetiQ Footcare'}</h1>}
        <p className="unit">{unit}</p>
        {address && <p className="addr">{address}</p>}
      </div>
      {action}
    </div>
  );
}

/** A numbered findings heading, or a plain one. */
export function Section({ title, sub, id }) {
  return (
    <h2 className="sec" id={id}>
      {title}
      {sub && <span className="secsub">{sub}</span>}
    </h2>
  );
}

/** Two-column label/value identification block. */
export function IdentTable({ rows }) {
  return (
    <table className="ident">
      <tbody>
        {rows.map((pair, i) => (
          <tr key={i}>
            <th>{pair[0]}</th>
            <td className={i === 0 ? 'strong' : undefined}>{pair[1] ?? '—'}</td>
            <th>{pair[2]}</th>
            <td>{pair[3] ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function KeyValues({ rows }) {
  const shown = rows.filter(r => r[1] !== undefined && r[1] !== null && r[1] !== '');
  if (!shown.length) return null;
  return (
    <table className="kv">
      <tbody>
        {shown.map(([k, v]) => (
          <tr key={k}><th>{k}</th><td>{v}</td></tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * A ruled data table.
 *
 * `cols` marks which columns are numeric so they right-align on tabular
 * figures — a column of readings that does not line up is harder to scan than
 * one that does, and this is a document people scan for outliers.
 */
export function DataTable({ caption, head, rows, numeric = [] }) {
  if (!rows.length) return null;
  return (
    <table className="data">
      {caption && <caption>{caption}</caption>}
      <thead>
        <tr>{head.map((h, i) => <th key={i} className={numeric.includes(i) ? 'n' : undefined}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri}>
            {r.map((cell, ci) => (
              <td key={ci} className={[numeric.includes(ci) ? 'n' : '', ci === head.length - 2 ? 'ref' : ''].filter(Boolean).join(' ') || undefined}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** A reading, coloured by its grade, with the grade named beside it. */
export function Reading({ degrees, severity, unit = '°' }) {
  if (typeof degrees !== 'number') return <>—</>;
  const abnormal = severity && severity !== 'normal';
  return (
    <span style={abnormal ? { color: severityColor(severity), fontWeight: 700 } : undefined}>
      {degrees.toFixed(1)}{unit}
    </span>
  );
}

/**
 * The graded finding beside a reading.
 *
 * `long` spells the normal case out as a clinician would dictate it. The
 * measurement tables sit in a half-width column where that phrase wraps to
 * three ragged lines, so they take the short form; the wider blocks take the
 * long one.
 */
export function Grade({ severity, long = false }) {
  if (!severity) return <span>Not measured</span>;
  if (!severity || severity === 'normal') {
    return <span style={{ color: '#3f7d4f' }}>{long ? 'Within normal limits' : 'Normal'}</span>;
  }
  return (
    <span style={{ color: severityColor(severity), fontWeight: 600 }}>{({ normal: 'Normal', mild: 'Mild', moderate: 'Moderate', severe: 'Severe' })[severity] || 'Not classified'}</span>
  );
}

/** A captioned clinical figure, with its provenance stated underneath. */
export function Figure({ url, num, caption, notes = [], title, view }) {
  if (!url) return null;
  return (
    <figure>
      {/* Names the picture and the view it came from, so a skeleton overlay
          is distinguishable from a pressure map at a glance rather than only
          from the caption underneath. */}
      {(title || view) && (
        <div className="fighead">
          <span className="figtitle">{title}</span>
          {view && <span className="figview">{view}</span>}
        </div>
      )}
      <img src={url} alt={caption} loading="eager" decoding="async" />
      <figcaption>
        <span className="fignum">Figure {num}.</span> {caption}
        {notes.filter(Boolean).map((n, i) => <span key={i}> {n}</span>)}
      </figcaption>
    </figure>
  );
}

export function Authentication({ clinician, date }) {
  return (
    <table className="sign">
      <tbody>
        <tr>
          <td>
            <p className="sigline" />
            <p className="signame">{clinician || '—'}</p>
            <p className="sigrole">Assessing clinician · prepared this report</p>
            <p className="sigdate">Date: {date || '—'}</p>
          </td>
          <td>
            <p className="sigline" />
            <p className="signame">&nbsp;</p>
            <p className="sigrole">Countersigned</p>
            <p className="sigdate">Date:</p>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/**
 * A tick label, pulled inside the track at either end so the outermost
 * figure is not sheared in half by the edge of the column.
 */
function Tick({ at, label }) {
  const edge = at <= 0.015 ? 'edge-l' : at >= 0.985 ? 'edge-r' : undefined;
  return <span className={edge} style={{ left: `${at * 100}%` }}>{label}</span>;
}

/**
 * A labelled actual-vs-reference bar for each measured posture angle.
 *
 * The table remains the source of the exact values. These bars make the same
 * values easier to scan and derive every boundary from the metric's stored
 * reference range, so the marker, colour and finding badge cannot disagree.
 */
export function AngleScales({ angles = [] }) {
  const measured = angles.filter(angle =>
    Number.isFinite(angle?.degrees) && rangeForAngle(angle));
  if (!measured.length) return null;

  return (
    <div className="metric-scales" aria-label="Actual angles against reference ranges">
      {measured.map((angle, index) => {
        const range = rangeForAngle(angle);
        const scale = bandScaleFor(range, severityCutsFor(angle.name));
        const at = positionOn(scale, angle.degrees);
        const severity = severityForAngle(angle);
        return (
          <div className="metric-scale" key={`${angle.name}-${index}`}>
            <div className="scalehead">
              <span className="scalename">{angle.name}</span>
              <span className="scaleref">Reference {referenceText(range)}</span>
            </div>
            <div className="plotrow above">
              <span className="mk" style={{ left: `${at * 100}%`, '--at': at, color: severityColor(severity) }}>
                Actual {angle.degrees.toFixed(1)}° · {({ normal: 'Normal', mild: 'Mild', moderate: 'Moderate', severe: 'Severe' })[severity]}
              </span>
            </div>
            <div className="track">
              {scale.bands.map((band, bandIndex) => (
                <i key={bandIndex} style={{ flexGrow: Math.max(0.0001, band.to - band.from), backgroundColor: band.color }} />
              ))}
            </div>
            <div className="ticks">
              {scale.ticks.map(t => <Tick key={t} at={positionOn(scale, t)} label={t} />)}
            </div>
            <div className="dirs">
              <span>{range[0] > 0 ? 'Below range' : 'Reference starts at 0°'}</span>
              <span className="mid">Normal {range[0]}–{range[1]}°</span>
              <span>Above range</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** A neutral 0–100 display bar for optical estimates that have no clinical range. */
export function OpticalScales({ rows = [] }) {
  const measured = rows.filter(row => Number.isFinite(Number(row.value)));
  if (!measured.length) return null;
  return (
    <div className="metric-scales optical-scales" aria-label="Optical estimate display bars">
      <p className="scale-note">Optical estimates use a 0–100 display scale. They are observations, not measured pressure or severity grades.</p>
      {measured.map((row, index) => {
        const value = Math.max(0, Math.min(100, Number(row.value)));
        return (
          <div className="metric-scale" key={`${row.label}-${index}`}>
            <div className="scalehead"><span className="scalename">{row.label}</span><span className="scaleref">Observed {value.toFixed(1)}</span></div>
            <div className="plotrow above"><span className="mk neutral" style={{ left: `${value}%`, '--at': (value) / 100 }}>Value {value.toFixed(1)}</span></div>
            <div className="track neutral-track"><i /></div>
            <div className="ticks"><Tick at={0} label={0} /><Tick at={0.5} label={50} /><Tick at={1} label={100} /></div>
            <div className="dirs"><span>Low estimate</span><span className="mid">Display midpoint</span><span>High estimate</span></div>
          </div>
        );
      })}
    </div>
  );
}

/** Bilateral load distribution with the recorded 50 ± 5% reference labelled. */
export function LoadShareScale({ bilateral }) {
  const right = Number(bilateral?.right);
  const left = Number(bilateral?.left);
  if (!Number.isFinite(right) && !Number.isFinite(left)) return null;
  const bands = [
    ['severe', 35], ['moderate', 5], ['mild', 5], ['normal', 10],
    ['mild', 5], ['moderate', 5], ['severe', 35],
  ];
  return (
    <div className="metric-scale load-scale">
      <div className="scalehead"><span className="scalename">Bilateral load distribution</span><span className="scaleref">Reference 50 ± 5%</span></div>
      <div className="plotrow above">{Number.isFinite(right) && <span className="mk" style={{ left: `${Math.max(0, Math.min(100, right))}%`, '--at': (Math.max(0, Math.min(100, right))) / 100 }}>R {right.toFixed(1)}%</span>}</div>
      <div className="track">{bands.map(([severity, width], i) => <i key={i} style={{ flexGrow: width, backgroundColor: severityColor(severity) }} />)}<span className="zero load-zero" /></div>
      <div className="plotrow below">{Number.isFinite(left) && <span className="mk" style={{ left: `${100 - Math.max(0, Math.min(100, left))}%`, '--at': (100 - Math.max(0, Math.min(100, left))) / 100 }}>L {left.toFixed(1)}%</span>}</div>
      <div className="ticks"><Tick at={0} label={0} /><Tick at={0.35} label={35} /><Tick at={0.45} label={45} /><Tick at={0.5} label={50} /><Tick at={0.55} label={55} /><Tick at={0.65} label={65} /><Tick at={1} label={100} /></div>
      <div className="dirs"><span>Left loading</span><span className="mid">Balanced</span><span>Right loading</span></div>
    </div>
  );
}

/**
 * A two-directional reference scale: normal at the centre, a named clinical
 * direction either way, both legs read on one track so the pair can be
 * compared at a glance. This is the rearfoot and lower-leg figure.
 *
 * All geometry comes from lib/metricBands; nothing here decides what is
 * normal. It is drawn from the scoped stylesheet above rather than from
 * utility classes, because this document is printed as often as it is read
 * and the printed sheet carries no utility stylesheet with it.
 */
export function BilateralScale({ title, range, cuts, leftLabel, rightLabel, left, right }) {
  const scale = bilateralScaleFor(range, cuts);
  const bands = [
    ...[...scale.bands].reverse().map(b => ({ ...b, from: -b.to, to: -b.from })),
    ...scale.bands,
  ];
  const sides = [['Left', left], ['Right', right]];

  return (
    <div className="scale">
      <div className="scalehead">
        <span className="scalename">{title}</span>
        <span className="scaleref">Reference {referenceText(range)}</span>
      </div>

      <table className="side">
        <thead>
          <tr><th>Side</th><th>Value</th><th>Direction</th><th>Finding</th></tr>
        </thead>
        <tbody>
          {sides.map(([tag, s]) => (
            <tr key={tag}>
              <td className="tag">{tag}</td>
              {s ? (
                <>
                  <td className="val" style={{ color: severityColor(s.severity) }}>
                    {s.degrees.toFixed(1)}°
                  </td>
                  <td className="dir">{s.text}</td>
                  <td><Grade severity={s.severity} long />
                    {s.confirmed && <span style={{ color: '#5b6670' }}> · clinician-confirmed</span>}
                  </td>
                </>
              ) : (
                <td colSpan={3} style={{ color: '#5b6670' }}>Not measured</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="plotrow above">
        {left && (
          <span className="mk" style={{ left: `${bilateralPosition(scale, left.degrees, left.side) * 100}%`, '--at': (bilateralPosition(scale, left.degrees, left.side) * 100) / 100, color: severityColor(left.severity) }}>
            L {left.degrees.toFixed(1)}°
          </span>
        )}
      </div>
      <div className="track">
        {bands.map((b, i) => (
          <i key={i} style={{ flexGrow: Math.max(0.0001, b.to - b.from), backgroundColor: b.color }} />
        ))}
        <span className="zero" />
      </div>
      <div className="plotrow below">
        {right && (
          <span className="mk" style={{ left: `${bilateralPosition(scale, right.degrees, right.side) * 100}%`, '--at': (bilateralPosition(scale, right.degrees, right.side) * 100) / 100, color: severityColor(right.severity) }}>
            R {right.degrees.toFixed(1)}°
          </span>
        )}
      </div>

      <div className="ticks">
        {scale.ticks.map(t => <Tick key={`l${t}`} at={bilateralPosition(scale, t, -1)} label={t} />)}
        <span style={{ left: '50%' }}>0</span>
        {scale.ticks.map(t => <Tick key={`r${t}`} at={bilateralPosition(scale, t, 1)} label={t} />)}
      </div>
      <div className="dirs">
        <span>{leftLabel}</span><span className="mid">Normal</span><span>{rightLabel}</span>
      </div>
    </div>
  );
}

/**
 * A passage of written assessment under a named, coloured heading.
 *
 * The narrative used to run as unbroken paragraphs. That is how a page of
 * clinical prose stops being read — a patient skims it, finds no entry point
 * and gives up — so each block now announces what it is before it says it,
 * and the colour says whether it is an observation, a finding or an action.
 *
 * `tone` is presentation only. Nothing here decides what is normal.
 */
export function Callout({ label, tone = 'neutral', children }) {
  return (
    <section className={tone === 'neutral' ? 'callout' : `callout ${tone}`}>
      {label && <h4>{label}</h4>}
      {children}
    </section>
  );
}
